import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  WarningAmber as WarningIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatAmount, formatDateOnly, initializeFormats, getFormatConfig } from '../../../utils/global';
import { formatActionTypeLabel, getActionType } from '../../../utils/chargebackStages';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDashboardPageSx,
  harmonateDashboardTitleSx,
  harmonateKpiCardSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateSectionHeaderSx,
  harmonateLinkSx,
  harmonateToolbarSx,
  harmonatePlainIconButtonSx,
  harmonateToolbarBuFieldSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { applyPropertyScope, assignedPropertyCodes, buildMdhViewAllPath } from './merchantDataHubNav';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('authToken') || localStorage.getItem('token');
const getTenantIntegrationId = () => localStorage.getItem('tenant_integration') || '';

const CREDIT_CARD_REPORTS_PATH = '/credit-card/transactions';
const CHARGEBACKS_PATH = '/chargeback/charge/default-charge';
const MDH_PROPERTY_STORAGE_KEY = 'merchant_data_hub_selected_property_id';
const MDH_PROPERTY_CODE_STORAGE_KEY = 'merchant_data_hub_selected_property_code';
const MDH_PROPERTY_NAME_STORAGE_KEY = 'merchant_data_hub_selected_property_name';
/** Keep in sync with Chargeback Management so View All can reuse the selection */
const CHARGEBACK_PROPERTY_STORAGE_KEY = 'normalized_chargebacks_selected_property_id';

const RECENT_PAGE_SIZE = 5;

/** Same reports as Credit Card Reports screen — hub preview toggles these */
const CC_HUB_REPORTS = [
  {
    id: 'credits_exceed_debits',
    label: 'Credits that Exceed Debits',
    endpoint: 'credit-card-fraud/credits-exceed-debits',
    reportId: 'fraud',
    subReportId: 'credits_exceed_debits',
  },
  {
    id: 'credits_without_debits',
    label: 'Credits without Debits',
    endpoint: 'credit-card-fraud/credits-without-debits',
    reportId: 'fraud',
    subReportId: 'credits_without_debits',
  },
  {
    id: 'chargeback_detail',
    label: 'Chargeback Detail',
    endpoint: 'credit-card-fraud/chargebacks/detail',
    reportId: 'chargeback_detail',
    subReportId: null,
  },
  {
    id: 'chargeback_win_loss',
    label: 'Chargeback Win/Loss',
    endpoint: 'credit-card-fraud/chargebacks/win-loss',
    reportId: 'chargeback_win_loss',
    subReportId: null,
  },
  {
    id: 'daily_settlement',
    label: 'Daily Settlement',
    endpoint: 'credit-card-fraud/daily-settlement',
    reportId: 'daily_settlement',
    subReportId: null,
  },
  {
    id: 'forced_authorization',
    label: 'Forced Authorization',
    endpoint: 'credit-card-fraud/forced-authorization',
    reportId: 'forced_authorization',
    subReportId: null,
  },
];

const getTenantPropertyOptionId = (option) =>
  option?.propertyId ?? option?.PROPERTY_ID ?? option?.property_id ?? null;

/** Snowflake PROPERTY_ID = Postgres property_code (dropdown label prefix before " - "). */
const propertyCodeForApi = (prop) => {
  if (!prop) return null;
  const explicit = prop.propertyCode ?? prop.PROPERTY_CODE;
  if (explicit != null && String(explicit).trim()) {
    return String(explicit).trim();
  }
  const label = prop.propertyName ?? prop.PROPERTY_NAME;
  if (label == null || !String(label).trim()) return null;
  const full = String(label).trim();
  const sep = ' - ';
  const i = full.indexOf(sep);
  if (i > 0) return full.slice(0, i).trim();
  return full;
};

const propertyLabel = (option) => {
  if (!option) return '';
  const code = option.propertyCode || option.PROPERTY_CODE || '';
  const name = option.propertyName || option.PROPERTY_NAME || '';
  return code && name ? `${code} - ${name}` : name || code || '';
};

const propertyToolbarFieldSx = {
  ...harmonateToolbarBuFieldSx,
  minWidth: 280,
  width: 320,
  '& .MuiAutocomplete-popupIndicator': { color: ht.white },
  '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255, 255, 255, 0.75)' },
  '& .MuiAutocomplete-endAdornment': { right: 6 },
  '& .MuiOutlinedInput-root': {
    ...harmonateToolbarBuFieldSx['& .MuiOutlinedInput-root'],
    paddingRight: '36px !important',
  },
};

const pickField = (row, keys) => {
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key];
    const upper = key.toUpperCase();
    if (row[upper] != null && row[upper] !== '') return row[upper];
    const lower = key.toLowerCase();
    if (row[lower] != null && row[lower] !== '') return row[lower];
  }
  return null;
};

const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** FastAPI list responses use pagination.total_items (not pagination.total). */
const getResponseTotal = (payload, fallbackList = []) => {
  if (!payload || typeof payload !== 'object') return fallbackList.length;
  const fromPagination =
    payload.pagination?.total_items ??
    payload.pagination?.total ??
    payload.pagination?.totalCount;
  const total =
    fromPagination ??
    payload.total_items ??
    payload.total ??
    payload.total_count ??
    payload.count;
  const n = Number(total);
  return Number.isFinite(n) ? n : fallbackList.length;
};

const getResponseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  return payload.data || payload.items || payload.results || payload.rows || [];
};

const KpiCard = ({ title, value, subtitle, icon, color, loading }) => (
  <Paper elevation={0} sx={harmonateKpiCardSx}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color,
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography variant="body2" sx={{ color: ht.text, fontWeight: 500, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: ht.text, mb: 0.5, minHeight: 32 }}>
      {loading ? <CircularProgress size={20} /> : value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" sx={{ color: ht.text }}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const MerchantDataHubDashboard = () => {
  const navigate = useNavigate();
  const mdhPerms = useFunctionPermissions(FC.MERCHANT_DATA_HUB);
  const canUseHub = Boolean(mdhPerms.canAccess || mdhPerms.canView);

  const [config, setConfig] = useState(getFormatConfig());
  const [loadingCc, setLoadingCc] = useState(false);
  const [loadingCb, setLoadingCb] = useState(false);
  const [error, setError] = useState(null);
  const [ccRows, setCcRows] = useState([]);
  const [ccTotal, setCcTotal] = useState(0);
  const [cbRows, setCbRows] = useState([]);
  const [cbTotal, setCbTotal] = useState(0);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [propertiesReady, setPropertiesReady] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedCcReportId, setSelectedCcReportId] = useState(CC_HUB_REPORTS[0].id);

  const selectedCcReport = useMemo(
    () => CC_HUB_REPORTS.find((r) => r.id === selectedCcReportId) || CC_HUB_REPORTS[0],
    [selectedCcReportId]
  );

  const selectedPropertyCode = useMemo(
    () => propertyCodeForApi(selectedProperty),
    [selectedProperty]
  );
  const userPropertyCodes = useMemo(() => assignedPropertyCodes(properties), [properties]);

  useEffect(() => {
    initializeFormats().then(() => setConfig(getFormatConfig())).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setPropertiesLoading(true);
        const token = getToken();
        const tenantId = localStorage.getItem('tenant_id');
        const userId = localStorage.getItem('user_id');
        if (!token || !tenantId || !userId) {
          setProperties([]);
          setSelectedProperty(null);
          return;
        }

        const apiUrl = `${getBaseUrl()}conductor/user-tenant-properties?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
        const resp = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!resp.ok) {
          throw new Error(`Failed to fetch properties: ${resp.status}`);
        }
        const data = await resp.json();
        const list = Array.isArray(data) ? data : [];
        setProperties(list);

        const savedId =
          sessionStorage.getItem(MDH_PROPERTY_STORAGE_KEY) ||
          localStorage.getItem(CHARGEBACK_PROPERTY_STORAGE_KEY);
        if (savedId && list.length > 0) {
          const match = list.find((p) => String(getTenantPropertyOptionId(p) ?? '') === savedId);
          if (match) setSelectedProperty(match);
        }
      } catch (e) {
        console.error('Error fetching properties for Merchant Data Hub:', e);
        setProperties([]);
      } finally {
        setPropertiesLoading(false);
        setPropertiesReady(true);
      }
    };

    fetchProperties();
  }, []);

  const handlePropertyChange = (_event, newValue) => {
    setSelectedProperty(newValue);
    const id = getTenantPropertyOptionId(newValue);
    const code = propertyCodeForApi(newValue);
    const name = newValue?.propertyName || newValue?.PROPERTY_NAME || '';
    if (id != null && id !== '') {
      sessionStorage.setItem(MDH_PROPERTY_STORAGE_KEY, String(id));
      localStorage.setItem(CHARGEBACK_PROPERTY_STORAGE_KEY, String(id));
    } else {
      sessionStorage.removeItem(MDH_PROPERTY_STORAGE_KEY);
      localStorage.removeItem(CHARGEBACK_PROPERTY_STORAGE_KEY);
    }
    if (code) {
      sessionStorage.setItem(MDH_PROPERTY_CODE_STORAGE_KEY, String(code));
      if (name) sessionStorage.setItem(MDH_PROPERTY_NAME_STORAGE_KEY, String(name));
      else sessionStorage.removeItem(MDH_PROPERTY_NAME_STORAGE_KEY);
    } else {
      sessionStorage.removeItem(MDH_PROPERTY_CODE_STORAGE_KEY);
      sessionStorage.removeItem(MDH_PROPERTY_NAME_STORAGE_KEY);
    }
  };

  const navigateViewAll = (basePath, extra = {}) => {
    navigate(
      buildMdhViewAllPath(basePath, {
        propertyCode: selectedPropertyCode || undefined,
        propertyName:
          selectedProperty?.propertyName ||
          selectedProperty?.PROPERTY_NAME ||
          undefined,
        ...extra,
      })
    );
  };

  const fetchCreditCardRecent = useCallback(async () => {
    if (!canUseHub) {
      setCcRows([]);
      setCcTotal(0);
      return;
    }
    const token = getToken();
    const tenantIntegrationId = getTenantIntegrationId();
    if (!token || !tenantIntegrationId) {
      setCcRows([]);
      setCcTotal(0);
      return;
    }

    setLoadingCc(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        page_size: String(RECENT_PAGE_SIZE),
      });
      const scope = applyPropertyScope(params, selectedPropertyCode, userPropertyCodes);
      const response = await fetch(
        `${getBaseUrl()}fastapi/v1/${selectedCcReport.endpoint}/${tenantIntegrationId}?${params}`,
        {
          method: scope.method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantIntegrationId,
          },
          body: scope.body,
        }
      );
      if (!response.ok) {
        throw new Error(`Credit card reports failed (${response.status})`);
      }
      const data = await response.json();
      const list = getResponseList(data);
      setCcRows(
        list.slice(0, RECENT_PAGE_SIZE).map((row, index) => ({
          id: pickField(row, ['id', 'ID', 'BATCH_ID', 'batchId', 'TRANSACTION_ID']) || `cc-${index}`,
          processor: pickField(row, ['PROCESSOR', 'Processor', 'processor']) || '—',
          merchantName:
            pickField(row, ['MERCHANT_NAME', 'Merchant Name', 'merchantName', 'MERCHANT_ID']) || '—',
          propertyName: pickField(row, ['PROPERTY_NAME', 'Property Name', 'propertyName']) || '—',
          transactionDate: pickField(row, [
            'TRANSACTION_DATE',
            'Transaction Date',
            'transactionDate',
            'SUBMIT_DATE',
          ]),
          amount: asNumber(
            pickField(row, [
              'PROCESSED_SALES_AMOUNT',
              'Processed Sales Amount',
              'processedSalesAmount',
              'AMOUNT',
              'amount',
            ])
          ),
          likelyFraud: pickField(row, ['IS_LIKELY_FRAUD', 'Is Likely Fraud', 'isLikelyFraud']),
        }))
      );
      setCcTotal(getResponseTotal(data, list));
    } catch (err) {
      console.error(err);
      setCcRows([]);
      setCcTotal(0);
      throw err;
    } finally {
      setLoadingCc(false);
    }
  }, [canUseHub, selectedPropertyCode, selectedCcReport, userPropertyCodes]);

  const fetchChargebackRecent = useCallback(async () => {
    if (!canUseHub) {
      setCbRows([]);
      setCbTotal(0);
      return;
    }
    const token = getToken();
    const tenantIntegrationId = getTenantIntegrationId();
    if (!token || !tenantIntegrationId) {
      setCbRows([]);
      setCbTotal(0);
      return;
    }

    setLoadingCb(true);
    try {
      // Same as Charge.jsx: resolve SCHEMA.VIEW_NAME from conductor config (category=CHARGEBACK)
      let schemaViewName = null;
      try {
        const configRes = await fetch(
          `${getBaseUrl()}conductor/config/default?category=${encodeURIComponent('CHARGEBACK')}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (configRes.ok) {
          const gridConfig = await configRes.json();
          schemaViewName = gridConfig?.category || null;
        }
      } catch (configErr) {
        console.warn('Chargeback grid config lookup failed:', configErr);
      }

      const params = new URLSearchParams({
        page: '1',
        page_size: String(RECENT_PAGE_SIZE),
      });
      if (schemaViewName && String(schemaViewName).includes('.')) {
        params.append('view_name', schemaViewName);
      }
      const scope = applyPropertyScope(params, selectedPropertyCode, userPropertyCodes);

      const response = await fetch(
        `${getBaseUrl()}fastapi/v1/chargebacks/${tenantIntegrationId}/normalized-chargebacks/?${params}`,
        {
          method: scope.method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantIntegrationId,
          },
          body: scope.body,
        }
      );
      if (!response.ok) {
        throw new Error(`Chargebacks failed (${response.status})`);
      }
      const data = await response.json();
      const list = getResponseList(data);
      setCbRows(
        list.slice(0, RECENT_PAGE_SIZE).map((row, index) => ({
          id:
            pickField(row, ['id', 'ID', 'CASE_ID', 'caseId', 'CHARGEBACK_ID']) || `cb-${index}`,
          reasonCode: pickField(row, ['REASON_CODE', 'Reason Code', 'reasonCode']) || '—',
          reason: pickField(row, ['REASON', 'Reason', 'reason']) || '—',
          chargebackAmount: asNumber(
            pickField(row, ['CHARGEBACK_AMOUNT', 'Chargeback Amount', 'chargebackAmount'])
          ),
          transactionAmount: asNumber(
            pickField(row, ['TRANSACTION_AMOUNT', 'Transaction Amount', 'transactionAmount'])
          ),
          cardType: pickField(row, ['CARD_TYPE', 'Card Type', 'cardType']) || '—',
          // Chargeback Management stages use ACTION_TYPE (not STATUS)
          status: formatActionTypeLabel(getActionType(row)),
        }))
      );
      setCbTotal(getResponseTotal(data, list));
    } catch (err) {
      console.error(err);
      setCbRows([]);
      setCbTotal(0);
      throw err;
    } finally {
      setLoadingCb(false);
    }
  }, [canUseHub, selectedPropertyCode, userPropertyCodes]);

  const loadDashboard = useCallback(async () => {
    setError(null);
    const results = await Promise.allSettled([fetchCreditCardRecent(), fetchChargebackRecent()]);
    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length === results.length && results.length > 0) {
      setError(failures[0].reason?.message || 'Failed to load Merchant Data Hub activity');
    } else if (failures.length) {
      setError(failures.map((f) => f.reason?.message).filter(Boolean).join(' · '));
    }
  }, [fetchCreditCardRecent, fetchChargebackRecent]);

  useEffect(() => {
    if (!propertiesReady) return;
    loadDashboard();
  }, [loadDashboard, propertiesReady]);

  const ccColumns = useMemo(
    () => [
      { field: 'processor', headerName: 'Processor', flex: 0.8, minWidth: 90 },
      { field: 'merchantName', headerName: 'Merchant', flex: 1.2, minWidth: 140 },
      { field: 'propertyName', headerName: 'Property', flex: 1, minWidth: 120 },
      {
        field: 'transactionDate',
        headerName: 'Txn Date',
        width: 110,
        valueFormatter: (params) =>
          params.value ? formatDateOnly(params.value, config.DateFormat) : '—',
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) =>
          params.value != null ? formatAmount(params.value, config.AmountFormat) : '—',
      },
      {
        field: 'likelyFraud',
        headerName: 'Likely Fraud',
        width: 110,
        renderCell: (params) => {
          const raw = String(params.value ?? '').toLowerCase();
          const isYes = raw === 'yes' || raw === 'true' || raw === '1' || raw === 'y';
          if (!params.value && params.value !== 0) return '—';
          return (
            <Chip
              label={isYes ? 'Yes' : 'No'}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                bgcolor: isYes ? 'rgba(239, 63, 63, 0.12)' : ht.listBg,
                color: isYes ? ht.red : ht.text,
              }}
            />
          );
        },
      },
    ],
    [config.AmountFormat, config.DateFormat]
  );

  const cbColumns = useMemo(
    () => [
      { field: 'reasonCode', headerName: 'Reason Code', flex: 1, minWidth: 120 },
      { field: 'reason', headerName: 'Reason', flex: 1.4, minWidth: 140 },
      {
        field: 'chargebackAmount',
        headerName: 'CB Amount',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) =>
          params.value != null ? formatAmount(params.value, config.AmountFormat) : '—',
      },
      { field: 'cardType', headerName: 'Card', width: 90 },
      {
        field: 'status',
        headerName: 'Action',
        width: 140,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: ht.text }}>
            {params.value || '—'}
          </Typography>
        ),
      },
    ],
    [config.AmountFormat]
  );

  const kpiCards = [
    {
      key: 'cc-rows',
      title: 'Credit Card Report Rows',
      value: canUseHub ? String(ccTotal) : '—',
      subtitle: selectedCcReport.label,
      icon: <CreditCardIcon fontSize="small" />,
      color: ht.text,
      loading: loadingCc,
    },
    {
      key: 'cc-recent',
      title: 'Recent CC Activity',
      value: canUseHub ? String(ccRows.length) : '—',
      subtitle: `Showing up to ${RECENT_PAGE_SIZE} rows`,
      icon: <AssessmentIcon fontSize="small" />,
      color: ht.green,
      loading: loadingCc,
    },
    {
      key: 'cb-total',
      title: 'Normalized Chargebacks',
      value: canUseHub ? String(cbTotal) : '—',
      subtitle: 'Total chargeback records',
      icon: <ReceiptIcon fontSize="small" />,
      color: ht.text,
      loading: loadingCb,
    },
    {
      key: 'cb-flagged',
      title: 'Chargeback Attention',
      value: canUseHub
        ? String(
            cbRows.filter((r) => {
              const s = String(r.status || '').toLowerCase();
              return s.includes('pending') || s.includes('expired') || s.includes('reject');
            }).length
          )
        : '—',
      subtitle: 'In recent sample',
      icon: <WarningIcon fontSize="small" />,
      color: ht.red,
      loading: loadingCb,
    },
  ];

  const loading = loadingCc || loadingCb;

  return (
    <Box sx={harmonateDashboardPageSx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography sx={harmonateDashboardTitleSx}>Merchant Data Hub</Typography>
        </Box>

        <Box sx={harmonateToolbarSx}>
          <Autocomplete
            size="small"
            options={properties}
            value={selectedProperty}
            onChange={handlePropertyChange}
            loading={propertiesLoading}
            getOptionLabel={propertyLabel}
            isOptionEqualToValue={(option, value) =>
              String(getTenantPropertyOptionId(option) ?? '') ===
              String(getTenantPropertyOptionId(value) ?? '')
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Property"
                placeholder="All properties"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {propertiesLoading ? (
                        <CircularProgress color="inherit" size={18} sx={{ color: ht.white }} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={propertyToolbarFieldSx}
          />
          <Tooltip title="Refresh">
            <IconButton
              onClick={loadDashboard}
              size="small"
              aria-label="Refresh Merchant Data Hub"
              sx={harmonatePlainIconButtonSx}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.key}>
            <KpiCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
        <Grid item xs={12} lg={7} sx={{ display: 'flex', minHeight: 0 }}>
          <Paper
            elevation={0}
            sx={{
              ...harmonateCardSx,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ ...harmonateSectionHeaderSx, flexShrink: 0, gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text, whiteSpace: 'nowrap' }}>
                  Credit Card Reports
                </Typography>
                {canUseHub && (
                  <FormControl
                    size="small"
                    sx={{
                      ...harmonateFormFieldsSx,
                      minWidth: 200,
                      maxWidth: 280,
                    }}
                  >
                    <InputLabel id="mdh-cc-report-label">Report</InputLabel>
                    <Select
                      labelId="mdh-cc-report-label"
                      id="mdh-cc-report"
                      label="Report"
                      value={selectedCcReportId}
                      onChange={(e) => setSelectedCcReportId(e.target.value)}
                      disabled={loadingCc}
                      MenuProps={harmonateSelectMenuProps}
                    >
                      {CC_HUB_REPORTS.map((report) => (
                        <MenuItem key={report.id} value={report.id}>
                          {report.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Button
                size="small"
                disabled={!canUseHub}
                onClick={() =>
                  navigateViewAll(CREDIT_CARD_REPORTS_PATH, {
                    report: selectedCcReport.reportId,
                    subReport: selectedCcReport.subReportId || undefined,
                  })
                }
                sx={{ ...harmonateLinkSx, fontSize: '0.875rem', flexShrink: 0 }}
              >
                View All
              </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {!canUseHub ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', px: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    You do not have access to Credit Card Reports.
                  </Typography>
                </Box>
              ) : loadingCc ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGridPro
                  rows={ccRows}
                  columns={ccColumns}
                  rowHeight={44}
                  hideFooterSelectedRowCount
                  disableRowSelectionOnClick
                  autoHeight={false}
                  pageSizeOptions={[5]}
                  initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  sx={{ ...harmonateDataGridSx, height: '100%' }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          No recent credit card report activity
                        </Typography>
                      </Box>
                    ),
                  }}
                />
              )}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${ht.line}`, bgcolor: ht.white, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {canUseHub
                  ? `Showing ${ccRows.length} of ${ccTotal} report rows`
                  : 'Credit Card Reports unavailable'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5} sx={{ display: 'flex', minHeight: 0 }}>
          <Paper
            elevation={0}
            sx={{
              ...harmonateCardSx,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ ...harmonateSectionHeaderSx, flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                Normalized Chargebacks
              </Typography>
              <Button
                size="small"
                disabled={!canUseHub}
                onClick={() => navigateViewAll(CHARGEBACKS_PATH)}
                sx={{ ...harmonateLinkSx, fontSize: '0.875rem' }}
              >
                View All
              </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {!canUseHub ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', px: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    You do not have access to Chargebacks.
                  </Typography>
                </Box>
              ) : loadingCb ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGridPro
                  rows={cbRows}
                  columns={cbColumns}
                  rowHeight={44}
                  hideFooterSelectedRowCount
                  disableRowSelectionOnClick
                  autoHeight={false}
                  pageSizeOptions={[5]}
                  initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  sx={{ ...harmonateDataGridSx, height: '100%' }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          No recent chargeback activity
                        </Typography>
                      </Box>
                    ),
                  }}
                />
              )}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${ht.line}`, bgcolor: ht.white, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {canUseHub
                  ? `Showing ${cbRows.length} of ${cbTotal} chargebacks`
                  : 'Chargebacks unavailable'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MerchantDataHubDashboard;
